import { useCallback, useEffect, useState } from 'react'
import {
  checkForUpdatesNow,
  getUpdaterState,
  installUpdateNow,
  onUpdaterState,
  setAutoUpdateEnabled,
  type UpdaterState
} from '@renderer/lib/runtime'

/**
 * Auto-updater state for the Settings panel.
 *
 * Returns null outside Electron, and on brands that ship no update feed — the
 * caller renders nothing in that case rather than an "unavailable" row.
 */
export function useAutoUpdate(): {
  state: UpdaterState | null
  busy: boolean
  setEnabled: (enabled: boolean) => Promise<void>
  checkNow: () => Promise<void>
  installNow: () => Promise<{ installed: boolean; reason?: string }>
} {
  const [state, setState] = useState<UpdaterState | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false

    void getUpdaterState().then((next) => {
      if (!cancelled) setState(next)
    })

    // Main pushes every transition (checking → downloading → ready), so the
    // panel follows a download that started long before Settings was opened.
    const unsubscribe = onUpdaterState((next) => setState(next))

    return (): void => {
      cancelled = true
      unsubscribe()
    }
  }, [])

  const setEnabled = useCallback(async (enabled: boolean): Promise<void> => {
    const next = await setAutoUpdateEnabled(enabled)
    if (next) setState(next)
  }, [])

  const checkNow = useCallback(async (): Promise<void> => {
    setBusy(true)
    try {
      await checkForUpdatesNow()
    } finally {
      setBusy(false)
    }
  }, [])

  const installNow = useCallback(async (): Promise<{ installed: boolean; reason?: string }> => {
    setBusy(true)
    try {
      return await installUpdateNow()
    } finally {
      setBusy(false)
    }
  }, [])

  return { state, busy, setEnabled, checkNow, installNow }
}
